import React, { useEffect, useState } from "react";
import {
    useGetTributePaymentStatsQuery,
    useGetTributePaymentListQuery,
    useGetTributePaymentSettingsQuery,
    useUpdateTributePaymentSettingsMutation,
    useResetTributeStatsMutation,
    useGetTributeWithdrawalsQuery,
    useCreateTributeWithdrawalMutation,
    useUpdateTributeWithdrawalMutation,
} from "../api";

const statusBadge = (status: string, isExpired: boolean): string => {
    if (status === "paid") return "bg-green-100 text-green-800";
    if (status === "pending") return isExpired ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800";
    if (status === "failed") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
};

const formatMoney = (value: unknown): string => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const todayIso = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const TributeStatsPage: React.FC = () => {
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [status, setStatus] = useState("");
    const [purpose, setPurpose] = useState("");
    const [source, setSource] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("paid_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [commissionPercent, setCommissionPercent] = useState("10");
    const [settingsSaveState, setSettingsSaveState] = useState<"idle" | "saved" | "error">("idle");

    const [walletOpen, setWalletOpen] = useState(false);
    const [addAmount, setAddAmount] = useState("");
    const [addDate, setAddDate] = useState(todayIso);
    const [addNote, setAddNote] = useState("");
    const [wdError, setWdError] = useState("");
    const [wdOk, setWdOk] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [editAmount, setEditAmount] = useState("");
    const [showResetTools, setShowResetTools] = useState(false);
    const [resetAmount, setResetAmount] = useState("");

    const {
        data: settingsData,
        isLoading: isLoadingSettings,
        refetch: refetchSettings,
    } = useGetTributePaymentSettingsQuery();
    const [updateSettings, { isLoading: isSavingSettings }] =
        useUpdateTributePaymentSettingsMutation();
    const [resetStats, { isLoading: isResetting }] = useResetTributeStatsMutation();
    const [createWithdrawal, { isLoading: isCreatingWd }] =
        useCreateTributeWithdrawalMutation();
    const [updateWithdrawal, { isLoading: isUpdatingWd }] =
        useUpdateTributeWithdrawalMutation();

    useEffect(() => {
        if (settingsData?.data) {
            setCommissionPercent(String(settingsData.data.commission_percent));
        }
    }, [settingsData]);

    useEffect(() => {
        if (!walletOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeWallet();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [walletOpen]);

    const statsParams = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        source: source || undefined,
        purpose: purpose || undefined,
    };

    const {
        data: statsData,
        isLoading: isLoadingStats,
        isError: isStatsError,
        error: statsError,
        refetch: refetchStats,
    } = useGetTributePaymentStatsQuery(statsParams, { pollingInterval: 60000 });

    const {
        data: listData,
        isLoading: isLoadingList,
        refetch: refetchList,
    } = useGetTributePaymentListQuery(
        {
            status: status || undefined,
            purpose: purpose || undefined,
            source: source || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            search: search || undefined,
            per_page: 20,
            page,
            sort_by: sortBy,
            sort_order: sortOrder,
        },
        { pollingInterval: 60000 }
    );

    const { data: withdrawalsData, refetch: refetchWithdrawals } =
        useGetTributeWithdrawalsQuery(undefined, { skip: !walletOpen });

    const payments = listData?.data || [];
    const meta = listData?.meta;
    const withdrawals = withdrawalsData?.data || [];

    const paid = statsData?.data?.paid ?? {
        count: 0,
        base_amount: 0,
        charge_amount: 0,
        commission_amount: 0,
    };
    const withdrawnAmount = statsData?.data?.withdrawn_amount ?? 0;
    const balanceAmount = statsData?.data?.balance_amount ?? paid.base_amount;
    const ops = statsData?.data?.ops ?? {
        pending_count: 0,
        stuck_pending_count: 0,
        cancelled_count: 0,
    };
    const byPurpose = statsData?.data?.by_purpose ?? {};
    const bySource = statsData?.data?.by_source ?? {};
    const statsFrom = settingsData?.data?.stats_from ?? statsData?.data?.stats_from ?? null;

    const addAmountNum = Number(addAmount);
    const addAmountOk = Number.isFinite(addAmountNum) && addAmountNum >= 0.01;
    const balanceAfter =
        addAmountOk ? Math.round((balanceAmount - addAmountNum) * 100) / 100 : balanceAmount;
    const overBalance = addAmountOk && addAmountNum > balanceAmount + 0.001;

    const refreshAll = () => {
        refetchSettings();
        refetchStats();
        refetchList();
        refetchWithdrawals();
    };

    const openWallet = () => {
        setAddAmount("");
        setAddDate(todayIso());
        setAddNote("");
        setWdError("");
        setWdOk(false);
        setEditId(null);
        setShowResetTools(false);
        setResetAmount(formatMoney(balanceAmount));
        setWalletOpen(true);
    };

    const closeWallet = () => {
        setWalletOpen(false);
        setEditId(null);
        setWdError("");
        setWdOk(false);
    };

    const saveCommission = async () => {
        setSettingsSaveState("idle");
        const value = Number(commissionPercent);
        if (!Number.isFinite(value) || value < 0 || value > 99) {
            setSettingsSaveState("error");
            return;
        }
        try {
            const response = await updateSettings({ commission_percent: value }).unwrap();
            setCommissionPercent(String(response.data.commission_percent));
            setSettingsSaveState("saved");
        } catch {
            setSettingsSaveState("error");
        }
    };

    const confirmReset = async () => {
        const amount = Number(resetAmount);
        if (!Number.isFinite(amount) || amount < 0) return;
        if (
            !window.confirm(
                "Обнулить период? Старые paid исчезнут из итогов, вывод запишется. Обычно достаточно «Вывод с кошелька»."
            )
        ) {
            return;
        }
        await resetStats({
            amount,
            description: "Вывод / обнуление",
        }).unwrap();
        setShowResetTools(false);
        setDateFrom("");
        setDateTo("");
        setPage(1);
        refreshAll();
    };

    const clearStatsFrom = async () => {
        if (!window.confirm("Убрать обнуление и снова считать все paid?")) return;
        await updateSettings({ stats_from: null }).unwrap();
        refreshAll();
    };

    const submitAddWithdrawal = async () => {
        setWdError("");
        setWdOk(false);
        const amount = Number(addAmount);
        if (!Number.isFinite(amount) || amount < 0.01) {
            setWdError("Введите сумму от 0.01");
            return;
        }
        try {
            await createWithdrawal({
                amount,
                withdrawn_at: addDate || undefined,
                description: addNote.trim() || "Вывод с кошелька",
            }).unwrap();
            setAddAmount("");
            setAddNote("");
            setAddDate(todayIso());
            setWdOk(true);
            refreshAll();
        } catch {
            setWdError("Не удалось сохранить вывод с кошелька");
        }
    };

    const saveEditWithdrawal = async () => {
        if (editId == null) return;
        const amount = Number(editAmount);
        if (!Number.isFinite(amount) || amount < 0.01) return;
        await updateWithdrawal({ id: editId, amount }).unwrap();
        setEditId(null);
        refreshAll();
    };

    const inputClass =
        "border rounded px-3 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100";

    return (
        <div className="space-y-6 max-w-full">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold mb-1 dark:text-gray-200">
                        Статистика Tribute
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Только paid. На Tribute — без нашей комиссии (base), чтобы сходилось с кабинетом.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        Комиссия, %
                    </span>
                    <input
                        type="number"
                        min={0}
                        max={99}
                        step={0.01}
                        value={commissionPercent}
                        onChange={(e) => {
                            setSettingsSaveState("idle");
                            setCommissionPercent(e.target.value);
                        }}
                        disabled={isLoadingSettings || isSavingSettings}
                        className={`${inputClass} w-20`}
                    />
                    <button
                        type="button"
                        onClick={saveCommission}
                        disabled={isLoadingSettings || isSavingSettings}
                        className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60"
                    >
                        {isSavingSettings ? "..." : "Сохранить"}
                    </button>
                    {settingsSaveState === "saved" && (
                        <span className="text-sm text-green-600">ок</span>
                    )}
                    {settingsSaveState === "error" && (
                        <span className="text-sm text-red-600">0–99</span>
                    )}
                </div>
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setPage(1);
                    refreshAll();
                }}
                className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 flex flex-wrap gap-4 items-end"
            >
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Дата с
                    </label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Дата по
                    </label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Статус
                    </label>
                    <select
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(1);
                            setSortBy(e.target.value === "paid" ? "paid_at" : "created_at");
                        }}
                        className={inputClass}
                    >
                        <option value="">Все</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Назначение
                    </label>
                    <select
                        value={purpose}
                        onChange={(e) => {
                            setPurpose(e.target.value);
                            setPage(1);
                        }}
                        className={inputClass}
                    >
                        <option value="">Все</option>
                        <option value="deposit">Deposit</option>
                        <option value="shop">Shop</option>
                        <option value="vip">VIP</option>
                        <option value="treasure">Treasure</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Источник
                    </label>
                    <select
                        value={source}
                        onChange={(e) => {
                            setSource(e.target.value);
                            setPage(1);
                        }}
                        className={inputClass}
                    >
                        <option value="">Все</option>
                        <option value="millennium">Millennium</option>
                        <option value="hub">Хаб</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Поиск
                    </label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="имя / фамилия / cookie / order"
                        className={`${inputClass} w-56`}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Сортировка
                    </label>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={inputClass}
                    >
                        <option value="paid_at">Дата оплаты</option>
                        <option value="created_at">Дата создания</option>
                        <option value="amount">Сумма</option>
                        <option value="status">Статус</option>
                        <option value="id">ID</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Порядок
                    </label>
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
                        className={inputClass}
                    >
                        <option value="desc">По убыванию</option>
                        <option value="asc">По возрастанию</option>
                    </select>
                </div>
                <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                >
                    Обновить
                </button>
            </form>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="text-sm dark:text-gray-300">
                        {statsFrom ? (
                            <>
                                Учёт с{" "}
                                <span className="font-semibold text-amber-700 dark:text-amber-300">
                                    {statsFrom}
                                </span>
                            </>
                        ) : (
                            <span className="text-gray-500">
                                Остаток = paid − выводы (без обнуления периода)
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={openWallet}
                            className="px-3 py-1.5 text-sm font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 shadow-sm"
                        >
                            Вывод с кошелька
                        </button>
                        {statsFrom && (
                            <button
                                type="button"
                                onClick={clearStatsFrom}
                                disabled={isSavingSettings}
                                className="px-2 py-1 text-xs text-gray-500 underline disabled:opacity-60"
                            >
                                Считать всё
                            </button>
                        )}
                    </div>
                </div>

                {isLoadingStats && (
                    <div className="text-sm text-gray-500">Загрузка статистики...</div>
                )}
                {isStatsError && (
                    <div className="text-sm text-red-600">
                        Ошибка stats API:{" "}
                        {(statsError as { status?: number })?.status ?? "unknown"}
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                        <div className="text-xs text-green-800 dark:text-green-300">
                            На Tribute, USD
                        </div>
                        <div className="text-xl font-semibold text-green-700 dark:text-green-300">
                            {formatMoney(paid.base_amount)}
                        </div>
                        <div className="text-[10px] text-green-700/70 dark:text-green-400/70">
                            без нашей комиссии · = баланс в Tribute
                        </div>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
                        <div className="text-xs text-amber-800 dark:text-amber-300">
                            Комиссия всего, USD
                        </div>
                        <div className="text-xl font-semibold text-amber-700 dark:text-amber-300">
                            {formatMoney(paid.commission_amount)}
                        </div>
                        <div className="text-[10px] text-amber-700/70">
                            сколько доплатили люди · оплатили {formatMoney(paid.charge_amount)}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={openWallet}
                        className="p-3 rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800 text-left hover:ring-2 hover:ring-sky-400 transition"
                    >
                        <div className="text-xs text-sky-800 dark:text-sky-300">
                            Выведено с Tribute, USD
                        </div>
                        <div className="text-xl font-semibold text-sky-700 dark:text-sky-300">
                            {formatMoney(withdrawnAmount)}
                        </div>
                        <div className="text-[10px] text-sky-700/70">клик — вывод с кошелька / история</div>
                    </button>
                    <button
                        type="button"
                        onClick={openWallet}
                        className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-left hover:ring-2 hover:ring-emerald-400 transition"
                    >
                        <div className="text-xs text-emerald-800 dark:text-emerald-300">
                            Остаток (админка), USD
                        </div>
                        <div className="text-xl font-semibold text-emerald-700 dark:text-emerald-300">
                            {formatMoney(balanceAmount)}
                        </div>
                        <div className="text-[10px] text-emerald-700/70">
                            на Tribute − выведено · {paid.count} paid
                        </div>
                    </button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                        <div className="text-xs text-gray-500">Pending</div>
                        <div className="text-lg font-semibold dark:text-gray-200">
                            {ops.pending_count}
                        </div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                        <div className="text-xs text-gray-500">Зависшие pending</div>
                        <div
                            className={`text-lg font-semibold ${
                                ops.stuck_pending_count > 0
                                    ? "text-red-600"
                                    : "dark:text-gray-200"
                            }`}
                        >
                            {ops.stuck_pending_count}
                        </div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                        <div className="text-xs text-gray-500">Cancelled</div>
                        <div className="text-lg font-semibold dark:text-gray-200">
                            {ops.cancelled_count}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(
                        [
                            ["По назначению", byPurpose],
                            ["По источнику", bySource],
                        ] as const
                    ).map(([title, group]) => (
                        <div key={title}>
                            <h2 className="text-lg font-semibold mb-2 dark:text-gray-200">
                                {title}
                            </h2>
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-2 pr-4 dark:text-gray-200">
                                            Ключ
                                        </th>
                                        <th className="text-right py-2 pr-4 dark:text-gray-200">
                                            Кол-во
                                        </th>
                                        <th className="text-right py-2 pr-4 dark:text-gray-200">
                                            На Tribute
                                        </th>
                                        <th className="text-right py-2 pr-4 dark:text-gray-200">
                                            Комиссия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(group).map(([key, s]) => (
                                        <tr
                                            key={key}
                                            className="border-b border-gray-100 dark:border-gray-800"
                                        >
                                            <td className="py-1 pr-4 dark:text-gray-300">
                                                {key === "hub"
                                                    ? "Хаб"
                                                    : key === "millennium"
                                                      ? "Millennium"
                                                      : key}
                                            </td>
                                            <td className="py-1 pr-4 text-right dark:text-gray-300">
                                                {s.count}
                                            </td>
                                            <td className="py-1 pr-4 text-right dark:text-gray-300">
                                                {formatMoney(s.base_amount)}
                                            </td>
                                            <td className="py-1 pr-4 text-right text-amber-700 dark:text-amber-300">
                                                {formatMoney(s.charge_amount - s.base_amount)}
                                            </td>
                                        </tr>
                                    ))}
                                    {Object.keys(group).length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="py-2 text-gray-500">
                                                Нет paid за период
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">
                    Список платежей
                </h2>
                {isLoadingList ? (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        Загрузка платежей...
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">ID</th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Пользователь
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        order_id
                                    </th>
                                    <th className="text-right py-2 pr-4 dark:text-gray-200">
                                        <div>Оплата</div>
                                        <div className="text-[10px] font-normal text-gray-500">
                                            на Tribute (без комиссии)
                                        </div>
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Назначение
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Источник
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Статус
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Создан
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Оплачен
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Детали
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => {
                                    const base =
                                        p.base_amount ??
                                        p.meta?.base_amount ??
                                        p.charge_amount ??
                                        p.amount;
                                    const charge = p.charge_amount ?? p.amount;
                                    const commission = Number(charge) - Number(base);
                                    const cur = (p.currency || "usdt").toUpperCase();

                                    return (
                                        <tr
                                            key={p.id}
                                            className="border-b border-gray-100 dark:border-gray-800"
                                        >
                                            <td className="py-2 pr-4 dark:text-gray-300">
                                                {p.id}
                                            </td>
                                            <td className="py-2 pr-4 dark:text-gray-300">
                                                {p.user_label || p.user_id || "—"}
                                            </td>
                                            <td className="py-2 pr-4 font-mono text-xs dark:text-gray-300">
                                                {p.order_id}
                                            </td>
                                            <td className="py-2 pr-4 text-right leading-tight">
                                                <div className="font-medium tabular-nums dark:text-gray-200">
                                                    {formatMoney(base)} {cur}
                                                </div>
                                                {Math.abs(commission) >= 0.005 && (
                                                    <>
                                                        <div className="text-[11px] text-gray-500 tabular-nums">
                                                            оплатил {formatMoney(charge)} {cur}
                                                        </div>
                                                        <div className="text-[11px] text-amber-700 dark:text-amber-300 tabular-nums">
                                                            комиссия {formatMoney(commission)}{" "}
                                                            {cur}
                                                        </div>
                                                    </>
                                                )}
                                            </td>
                                            <td className="py-2 pr-4 dark:text-gray-300">
                                                {p.purpose}
                                            </td>
                                            <td className="py-2 pr-4">
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                                        p.source === "hub"
                                                            ? "bg-indigo-100 text-indigo-800"
                                                            : "bg-sky-100 text-sky-800"
                                                    }`}
                                                >
                                                    {p.source === "hub"
                                                        ? "Хаб"
                                                        : "Millennium"}
                                                </span>
                                            </td>
                                            <td className="py-2 pr-4">
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-medium ${statusBadge(
                                                        p.status,
                                                        p.is_expired
                                                    )}`}
                                                >
                                                    {p.status}
                                                    {p.is_expired ? " (истёк)" : ""}
                                                </span>
                                            </td>
                                            <td className="py-2 pr-4 text-xs dark:text-gray-300">
                                                {p.created_at || "—"}
                                            </td>
                                            <td className="py-2 pr-4 text-xs dark:text-gray-300">
                                                {p.paid_at || "—"}
                                            </td>
                                            <td className="py-2 pr-4">
                                                <details>
                                                    <summary className="cursor-pointer text-blue-600 text-xs">
                                                        meta
                                                    </summary>
                                                    <pre className="mt-1 max-w-md whitespace-pre-wrap break-all text-[11px] text-gray-600 dark:text-gray-400">
                                                        {JSON.stringify(p.meta ?? {}, null, 2)}
                                                    </pre>
                                                </details>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {payments.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={10}
                                            className="py-4 text-center text-gray-500"
                                        >
                                            Платежей не найдено
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {meta && meta.last_page > 1 && (
                    <div className="flex items-center justify-between mt-4 text-sm dark:text-gray-300">
                        <div>
                            Показано {meta.from}–{meta.to} из {meta.total}
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={meta.current_page <= 1}
                                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                            >
                                Назад
                            </button>
                            <span>
                                {meta.current_page} / {meta.last_page}
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    setPage((p) => Math.min(meta.last_page, p + 1))
                                }
                                disabled={meta.current_page >= meta.last_page}
                                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                            >
                                Вперёд
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {walletOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeWallet();
                    }}
                    role="presentation"
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="tribute-wallet-title"
                        className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-800 shadow-2xl max-h-[92vh] overflow-y-auto"
                    >
                        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 pt-5 pb-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-b border-gray-100 dark:border-gray-700">
                            <div>
                                <h3
                                    id="tribute-wallet-title"
                                    className="text-lg font-semibold dark:text-gray-100"
                                >
                                    Вывод с кошелька
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Сумма отнимается от остатка. История выводов сохраняется.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeWallet}
                                className="shrink-0 rounded-lg px-2.5 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-5 space-y-5">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="rounded-xl bg-green-50 dark:bg-green-950/40 border border-green-200/80 dark:border-green-800 px-3 py-2.5">
                                    <div className="text-[10px] uppercase tracking-wide text-green-700/80 dark:text-green-400">
                                        На Tribute
                                    </div>
                                    <div className="text-lg font-semibold tabular-nums text-green-800 dark:text-green-300">
                                        {formatMoney(paid.base_amount)}
                                    </div>
                                </div>
                                <div className="rounded-xl bg-sky-50 dark:bg-sky-950/40 border border-sky-200/80 dark:border-sky-800 px-3 py-2.5">
                                    <div className="text-[10px] uppercase tracking-wide text-sky-700/80 dark:text-sky-400">
                                        Выведено
                                    </div>
                                    <div className="text-lg font-semibold tabular-nums text-sky-800 dark:text-sky-300">
                                        {formatMoney(withdrawnAmount)}
                                    </div>
                                </div>
                                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/80 dark:border-emerald-800 px-3 py-2.5">
                                    <div className="text-[10px] uppercase tracking-wide text-emerald-700/80 dark:text-emerald-400">
                                        Остаток
                                    </div>
                                    <div className="text-lg font-semibold tabular-nums text-emerald-800 dark:text-emerald-300">
                                        {formatMoney(balanceAmount)}
                                    </div>
                                </div>
                            </div>

                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    void submitAddWithdrawal();
                                }}
                                className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-950/20 p-4 space-y-3"
                            >
                                <div className="text-sm font-medium text-sky-900 dark:text-sky-200">
                                    Вывод с кошелька (−)
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs mb-1 text-gray-600 dark:text-gray-400">
                                            Сумма, USD
                                        </label>
                                        <input
                                            type="number"
                                            min={0.01}
                                            step={0.01}
                                            autoFocus
                                            value={addAmount}
                                            onChange={(e) => {
                                                setWdOk(false);
                                                setWdError("");
                                                setAddAmount(e.target.value);
                                            }}
                                            placeholder="0.00"
                                            className={`${inputClass} w-full text-lg font-semibold tabular-nums py-2`}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs mb-1 text-gray-600 dark:text-gray-400">
                                            Дата вывода
                                        </label>
                                        <input
                                            type="date"
                                            value={addDate}
                                            onChange={(e) => setAddDate(e.target.value)}
                                            className={`${inputClass} w-full py-2`}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-600 dark:text-gray-400">
                                        Комментарий (необязательно)
                                    </label>
                                    <input
                                        type="text"
                                        value={addNote}
                                        onChange={(e) => setAddNote(e.target.value)}
                                        placeholder="Напр. вывод на карту / USDT"
                                        maxLength={255}
                                        className={`${inputClass} w-full`}
                                    />
                                </div>

                                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                                    <div className="tabular-nums dark:text-gray-300">
                                        После записи остаток:{" "}
                                        <span
                                            className={`font-semibold ${
                                                overBalance
                                                    ? "text-amber-600"
                                                    : "text-emerald-700 dark:text-emerald-300"
                                            }`}
                                        >
                                            {formatMoney(balanceAfter)}
                                        </span>
                                        {overBalance && (
                                            <span className="ml-2 text-xs text-amber-600">
                                                больше текущего остатка
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isCreatingWd || !addAmountOk}
                                        className="px-4 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-50 shadow-sm"
                                    >
                                        {isCreatingWd ? "Сохранение…" : "Вывод с кошелька"}
                                    </button>
                                </div>
                                {wdError && (
                                    <div className="text-sm text-red-600">{wdError}</div>
                                )}
                                {wdOk && (
                                    <div className="text-sm text-green-600">
                                        Вывод с кошелька сохранён, остаток обновлён
                                    </div>
                                )}
                            </form>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold dark:text-gray-200">
                                        История выводов
                                    </h4>
                                    <span className="text-xs text-gray-500">
                                        {withdrawals.length} запис.
                                    </span>
                                </div>
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-900/60">
                                            <tr>
                                                <th className="text-left py-2 px-3 dark:text-gray-300 font-medium">
                                                    Дата
                                                </th>
                                                <th className="text-right py-2 px-3 dark:text-gray-300 font-medium">
                                                    Сумма
                                                </th>
                                                <th className="text-left py-2 px-3 dark:text-gray-300 font-medium">
                                                    Описание
                                                </th>
                                                <th className="text-right py-2 px-3 dark:text-gray-300 font-medium" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {withdrawals.map((w) => (
                                                <tr
                                                    key={w.id}
                                                    className="border-t border-gray-100 dark:border-gray-800"
                                                >
                                                    <td className="py-2 px-3 dark:text-gray-300 whitespace-nowrap">
                                                        {w.withdrawn_at || "—"}
                                                    </td>
                                                    <td className="py-2 px-3 text-right tabular-nums font-medium dark:text-gray-100">
                                                        {editId === w.id ? (
                                                            <input
                                                                type="number"
                                                                step={0.01}
                                                                value={editAmount}
                                                                onChange={(e) =>
                                                                    setEditAmount(e.target.value)
                                                                }
                                                                className={`${inputClass} w-24 text-right`}
                                                            />
                                                        ) : (
                                                            <span className="text-sky-700 dark:text-sky-300">
                                                                −{formatMoney(w.amount)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-500 text-xs max-w-[10rem] truncate">
                                                        {w.description || "—"}
                                                    </td>
                                                    <td className="py-2 px-3 text-right whitespace-nowrap">
                                                        {editId === w.id ? (
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditId(null)}
                                                                    className="text-xs text-gray-500"
                                                                >
                                                                    отмена
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={saveEditWithdrawal}
                                                                    disabled={isUpdatingWd}
                                                                    className="text-xs text-green-600 font-medium"
                                                                >
                                                                    сохранить
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setEditId(w.id);
                                                                    setEditAmount(String(w.amount));
                                                                }}
                                                                className="text-xs text-blue-600"
                                                            >
                                                                править
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {withdrawals.length === 0 && (
                                                <tr>
                                                    <td
                                                        colSpan={4}
                                                        className="py-8 text-center text-gray-500"
                                                    >
                                                        Пока нет выводов — запишите первый выше
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <details
                                className="rounded-xl border border-amber-200/80 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/20"
                                open={showResetTools}
                                onToggle={(e) =>
                                    setShowResetTools((e.target as HTMLDetailsElement).open)
                                }
                            >
                                <summary className="cursor-pointer select-none px-4 py-2.5 text-xs text-amber-800 dark:text-amber-300">
                                    Редко: обнулить период учёта (не нужно для обычного вывода)
                                </summary>
                                <div className="px-4 pb-4 space-y-3 border-t border-amber-200/60 dark:border-amber-900/40 pt-3">
                                    <p className="text-xs text-amber-900/80 dark:text-amber-200/70">
                                        Запишет вывод и начнёт учёт с сегодня с нуля. Для обычного
                                        вывода денег с кабинета Tribute используйте форму выше.
                                    </p>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <div>
                                            <label className="block text-xs mb-1 text-gray-500">
                                                Сумма в историю
                                            </label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                value={resetAmount}
                                                onChange={(e) => setResetAmount(e.target.value)}
                                                className={`${inputClass} w-32`}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={confirmReset}
                                            disabled={isResetting}
                                            className="px-3 py-1.5 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-60"
                                        >
                                            {isResetting ? "…" : "Обнулить с сегодня"}
                                        </button>
                                    </div>
                                </div>
                            </details>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TributeStatsPage;
